import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { IzzyTicketNumberSequenceEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

const DEFAULT_PREFIX = 'TKT';

/**
 * Ticket Numbering admin panel (PRD v4.0 §5.2). Org-scoped configuration of the per-org ticket
 * prefix (e.g. "ACME" → ACME-0042). Each org numbers its tickets independently.
 *
 * Backed by the izzy.TicketNumberSequence entity — read/written via RunView + Save exactly like
 * the SLA Policies panel reads SLAPolicy. The row holds TicketPrefix (admin-editable here) and
 * NextSequenceNumber (maintained only by spAssignNextTicketNumber at ticket creation — never written
 * from the UI). If no row exists yet for the org, saving creates it.
 */
@Component({
  standalone: false,
  selector: 'izzy-ticket-numbering',
  templateUrl: './ticket-numbering.component.html',
  styleUrls: ['./ticket-numbering.component.css'],
})
export class TicketNumberingComponent implements OnInit, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  /** The org's sequence row (holds prefix + counter); null until the org's first ticket or first save. */
  private sequence: IzzyTicketNumberSequenceEntity | null = null;

  protected prefix = DEFAULT_PREFIX;
  protected nextNumber = 1;

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    if (!this.organizationId) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const rv = new RunView();
      const res = await rv.RunView<IzzyTicketNumberSequenceEntity>({
        EntityName: 'Ticket Number Sequences',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        ResultType: 'entity_object',
        MaxRows: 1,
      });
      this.sequence = (res.Success && res.Results.length > 0) ? res.Results[0] : null;
      this.prefix = (this.sequence?.TicketPrefix || DEFAULT_PREFIX).trim() || DEFAULT_PREFIX;
      this.nextNumber = this.sequence?.NextSequenceNumber ?? 1;
    } catch (error) {
      console.error('TicketNumbering: failed to load sequence', error);
      this.errorMessage = 'Failed to load ticket numbering configuration.';
    } finally {
      this.isLoading = false;
      this.emitStatus();
    }
  }

  /** A valid prefix: 1–20 chars, letters/digits/hyphen. */
  protected get prefixValid(): boolean {
    const p = (this.prefix || '').trim();
    return p.length >= 1 && p.length <= 20 && /^[A-Za-z0-9-]+$/.test(p);
  }

  /** Example of what new tickets will look like with the current prefix. */
  protected get previewLabel(): string {
    const p = (this.prefix || DEFAULT_PREFIX).trim() || DEFAULT_PREFIX;
    return `${p}-${String(this.nextNumber).padStart(4, '0')}`;
  }

  async savePrefix(): Promise<void> {
    if (!this.organizationId || !this.prefixValid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';
    const newPrefix = this.prefix.trim();
    try {
      const md = new Metadata();
      let seq = this.sequence;
      if (!seq) {
        // No row yet for this org — create it (the admin set a prefix before the first ticket).
        // ID auto-generates; NextSequenceNumber defaults to 1. Mirrors how SLA Policies creates a row.
        seq = await md.GetEntityObject<IzzyTicketNumberSequenceEntity>('Ticket Number Sequences');
        seq.NewRecord();
        seq.OrganizationID = this.organizationId;
      }
      seq.TicketPrefix = newPrefix;
      if (!await seq.Save()) {
        this.errorMessage = 'Failed to save the prefix.';
        return;
      }
      this.sequence = seq;
      this.prefix = newPrefix;
      this.nextNumber = seq.NextSequenceNumber ?? 1;
      this.successMessage = `Ticket prefix saved — new tickets will be numbered ${this.previewLabel}.`;
      this.emitStatus();
    } catch (error) {
      console.error('TicketNumbering: failed to save prefix', error);
      this.errorMessage = 'Failed to save the prefix.';
    } finally {
      this.isSaving = false;
    }
  }

  private emitStatus(): void {
    if ((this.prefix || DEFAULT_PREFIX).trim().toUpperCase() === DEFAULT_PREFIX) {
      this.statusChange.emit({ status: 'warning', tooltip: 'Using the default TKT prefix — set a custom per-org prefix' });
    } else {
      this.statusChange.emit({ status: 'good', tooltip: `Ticket prefix: ${this.prefix}` });
    }
  }
}

/** Tree-shake anchor — forces this component to load so its @Component registers. */
export function LoadTicketNumberingComponent(): void {}
